import { Close, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography
} from "@mui/material";
import { formatDistance } from "date-fns";
import React, { useMemo, useState } from "react";
import { t } from "ttag";
import RedisIcon from "../../components/RedisIcon/RedisIcon";
import { IRedisDetails, IRedisInfo } from "../../models/Queues.types";
import useStyles from "./RedisInfoPopup.style";

interface IRedisInfoPopupProps {
  /** null while the details are loading */
  details: IRedisDetails | null;
  onClose: () => void;
}

const BYTE_UNITS = ["B", "kB", "MB", "GB", "TB"];

// Decimal units with three significant digits, the way bull-board's own Redis panel prints sizes.
const formatBytes = (bytes: number) => {
  if (!(bytes >= 1)) {
    return "0 B";
  }

  const unit = Math.min(Math.floor(Math.log10(bytes) / 3), BYTE_UNITS.length - 1);
  const value = bytes / 1000 ** unit;
  const digits = Math.max(3, Math.floor(value).toString().length);

  return `${Number(value.toPrecision(digits))} ${BYTE_UNITS[unit]}`;
};

const formatUptime = (seconds: string) => formatDistance(0, Number(seconds) * 1000, { includeSeconds: true });

const formatCount = (value: string) => Number(value).toLocaleString();

/** Keys across every database, from the keyspace lines (`db0:keys=5,expires=0,...`); null when none were reported. */
const countKeys = (info: IRedisInfo) => {
  const databases = Object.keys(info).filter((key) => /^db\d+$/.test(key));

  return databases.length
    ? databases.reduce((total, key) => total + (Number(/keys=(\d+)/.exec(info[key])?.[1]) || 0), 0)
    : null;
};

const RedisInfoPopup: React.FC<IRedisInfoPopupProps> = ({ details, onClose }) => {
  const classes: any = useStyles();
  const [filter, setFilter] = useState<string>("");
  const [openSections, setOpenSections] = useState<string[]>([]);

  const renderRow = (label: string, value: React.ReactNode) => (
    <div className={classes.row} key={label}>
      <Typography className={classes.label}>{label}</Typography>
      <div className={classes.value}>{value}</div>
    </div>
  );

  const renderMemoryUsage = (info: IRedisInfo) => {
    const used = Number(info.used_memory);

    if (!used) {
      return null;
    }

    // Redis reports maxmemory 0 when it has no cap, so the machine's memory is the limit then.
    const limit = Number(info.maxmemory) || Number(info.total_system_memory);

    return renderRow(
      t`Memory usage`,
      limit ? (
        <>
          <span className={classes.valueDetail}>
            {formatBytes(used)} {t`of`} {formatBytes(limit)}
          </span>
          <span>{`${((used / limit) * 100).toFixed(2)}%`}</span>
        </>
      ) : (
        formatBytes(used)
      )
    );
  };

  const renderHitRate = (info: IRedisInfo) => {
    const hits = Number(info.keyspace_hits);
    const misses = Number(info.keyspace_misses);

    if (!hits && !misses) {
      return null;
    }

    return renderRow(t`Cache hit rate`, `${((hits / (hits + misses)) * 100).toFixed(1)}%`);
  };

  const renderSummary = (info: IRedisInfo, dbSize?: number | null) => {
    const keys = dbSize ?? countKeys(info);
    const rows = [
      renderMemoryUsage(info),
      !!Number(info.used_memory_peak) && renderRow(t`Peak memory usage`, formatBytes(Number(info.used_memory_peak))),
      !!info.mem_fragmentation_ratio && renderRow(t`Fragmentation ratio`, info.mem_fragmentation_ratio),
      !!info.connected_clients && renderRow(t`Connected clients`, info.connected_clients),
      !!info.blocked_clients && renderRow(t`Blocked clients`, info.blocked_clients),
      !!info.redis_version &&
        renderRow(
          t`Version`,
          info.valkey_version ? `${info.redis_version} (Valkey ${info.valkey_version})` : info.redis_version
        ),
      !!info.redis_mode && renderRow(t`Mode`, info.redis_mode),
      !!info.os && renderRow(t`OS`, info.os),
      !!info.uptime_in_seconds && renderRow(t`Up time`, formatUptime(info.uptime_in_seconds)),
      !!info.role && renderRow(t`Role`, info.role),
      !!info.instantaneous_ops_per_sec && renderRow(t`Operations per second`, formatCount(info.instantaneous_ops_per_sec)),
      !!info.total_commands_processed && renderRow(t`Commands processed`, formatCount(info.total_commands_processed)),
      !!info.total_connections_received &&
        renderRow(t`Connections received`, formatCount(info.total_connections_received)),
      keys !== null && keys !== undefined && renderRow(t`Keys`, formatCount(String(keys))),
      renderHitRate(info),
      !!info.expired_keys && renderRow(t`Expired keys`, formatCount(info.expired_keys)),
      !!info.evicted_keys && renderRow(t`Evicted keys`, formatCount(info.evicted_keys)),
      !!info.maxmemory_policy && renderRow(t`Eviction policy`, info.maxmemory_policy)
    ].filter(Boolean);

    if (!rows.length) {
      return <Typography className={classes.emptyText}>{t`Redis did not report any details.`}</Typography>;
    }

    return <div>{rows}</div>;
  };

  const visibleSections = useMemo(() => {
    const term = filter.trim().toLowerCase();

    return (details?.sections || [])
      .map((section) => ({
        name: section.name,
        fields: Object.entries(section.fields).filter(
          ([key, value]) => !term || key.toLowerCase().includes(term) || value.toLowerCase().includes(term)
        )
      }))
      .filter((section) => section.fields.length);
  }, [details, filter]);

  const renderAllFields = () => {
    if (!details?.sections?.length) {
      return null;
    }

    return (
      <>
        <Divider className={classes.divider} />
        <div className={classes.fieldsHeader}>
          <Typography className={classes.sectionTitle}>{t`All fields`}</Typography>
          <TextField
            size="small"
            placeholder={t`Filter fields`}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        </div>
        {!visibleSections.length && <Typography className={classes.emptyText}>{t`No matching fields.`}</Typography>}
        {visibleSections.map((section) => (
          <Accordion
            key={section.name}
            disableGutters
            elevation={0}
            className={classes.accordion}
            // While filtering, matches open up so they are not hidden behind a collapsed section.
            expanded={!!filter.trim() || openSections.includes(section.name)}
            onChange={(_event, isOpen) =>
              setOpenSections((open) => (isOpen ? [...open, section.name] : open.filter((name) => name !== section.name)))
            }
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography className={classes.accordionTitle}>
                {section.name} <span className={classes.fieldCount}>({section.fields.length})</span>
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              {section.fields.map(([key, value]) => (
                <div className={classes.fieldRow} key={key}>
                  <span className={classes.fieldKey}>{key}</span>
                  <span className={classes.fieldValue}>{value}</span>
                </div>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </>
    );
  };

  const renderContent = () => {
    if (!details) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      );
    }

    return (
      <>
        {renderSummary(details.info, details.dbSize)}
        {renderAllFields()}
      </>
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <div className={classes.titleLeft}>
          <RedisIcon />
          <Typography className={classes.titleText}>{t`Redis details`}</Typography>
        </div>
        <IconButton size="small" aria-label={t`Close`} onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>{renderContent()}</DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose}>{t`Close`}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RedisInfoPopup;
